import { useRef, useEffect, useState, useMemo } from "react";
import styled from "styled-components";
import Modal from "../modal/Modal";
import SelectMenuItem from "./SelectMenuItem";
import useTranslation from "../../hooks/useTranslation";
import { TextControl } from "../generic/Core";
import IconButton from "../generic/IconButton";
import { Icon_Plus, Icon_Arrow_Left, Icon_Arrow_Right, Icon_Check } from "../../styles/icons";
import ReactDOM from "react-dom";
import { Button } from "../generic/Core";
import { useModalStack } from "../../context/modalStackContext";
import ModalCard from "../modal/ModalCard";

//Import KeyPress Utils:
import useKeyPress from "../../hooks/useKeyPress";

const SearchField = styled(TextControl)``;

const ListGroup = styled.ul`
  --list-group-border-radius: 0.5rem;
  display: flex;
  flex-direction: column;
  padding-left: 0;
  margin-bottom: 0;
  margin-right: 0.5rem;
  border-radius: var(--list-group-border-radius);
  overflow-x: hidden;
  min-height: 40rem;
  list-style: none;
  gap: 0.5rem;
`;

const SearchFieldContainer = styled.div`
    display: flex;
    gap: 0.5rem;
    align-items: center;
`

const StepHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 1rem;
//    padding: 1rem 0;
//    border-bottom: 1px solid ${({ theme }) => theme.modal.divider};
    margin-bottom: 1rem;
`

const SelectedParentDisplay = styled.div`
    background-color: ${({ theme }) => theme.dropdown.selectedBackground};
    color: ${({ theme }) => theme.dropdown.selectedFont};
    padding: 0.75rem 1rem;
    border-radius: 0.375rem;
    flex: 1;
    font-weight: 500;
`

const SearchResults = styled.div`
  position: absolute;
  top: ${props => props.$hasHeader ? '130px' : '65px'};
  width: calc(100% - 20px);
  max-height: calc(100% - ${props => props.$hasHeader ? '200px' : '140px'});
  overflow-y: auto;
  z-index: 101;

  &.visible {
    visibility: visible;
  }

  &.invisible {
    visibility: hidden !important;
  }
`;

const ActionButtons = styled.div`
    position: absolute;
    bottom: 20px;
    right: 20px;
    display: flex;
    gap: 0.5rem;
`

const HierarchicalMenuItem = styled(SelectMenuItem)`
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    
    &:hover {
        background-color: ${({ theme }) => theme.dropdown.active};
    }
`

const MenuItemContent = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    border: 1px solid ${({ theme }) => theme.area.lightBorder};
    border-bottom: 1px solid ${({ theme }) => theme.modal?.divider || theme.area.lightBorder};
    border-radius: 0.5rem;
    padding: 0.75rem 1rem;
    cursor: pointer;
    background-color: ${({ $active, theme }) => $active ? theme.dropdown.active : 'transparent'};

    &:hover {
        background-color: ${({ theme }) => theme.dropdown.active};
    }
`

const ItemText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
`

const ItemSubtitle = styled.span`
    font-size: 0.8rem;
    opacity: 0.7;
`

const HConnectModal = ({
    data,
    valueName,
    isVisible,
    setIsVisible,
    onSelect,
    entity,
    modalTitle,
    modalOpacity,
    parentId,
    parentField,
    options,
    persistValue,
    testId,
    origin = false,
    zIndex,
    onClose
}) => {

    const { t } = useTranslation();
    const [cursor, setCursor] = useState(-1);
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [currentStep, setCurrentStep] = useState(1);
    const [selectedParent, setSelectedParent] = useState(null);
    const [selectedChild, setSelectedChild] = useState(null);

    const searchResultRef = useRef(null);
    const focusRef = useRef(null);

    const { pushModal } = useModalStack();

    /*
     * `options.pickGroup: false` — only a child can be picked: a group is a way
     * in, never a value (an expense category is an account, its group is not).
     * The ✓ and + buttons go, and a search typed on step 1 looks through every
     * group's children at once, so "electricity" finds the account without the
     * user having to know it sits under premises.
     */
    const pickGroup = options?.pickGroup !== false;
    const searchAcross = !pickGroup && currentStep === 1 && debouncedSearch.length > 0;

    // Handle search input changes
    const handleSearchChange = (e) => {
        setSearch(e.target.value);
    };

    // Debounce search — filter only runs 150 ms after the user stops typing
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), 150);
        return () => clearTimeout(timer);
    }, [search]);

    const scrollIntoView = (position) => {
        if (searchResultRef.current) {
            searchResultRef.current.scrollTo({
                top: position,
                behavior: "smooth",
            });
        }
    };

    const clickAction = () => {
        const options={ mode: 'new' }

        // Push the card onto the stack - no need for local state to track visibility
        pushModal(ModalCard, {
            entity,
            origin,
            options,
            isVisible: true, // Always true when pushed to stack
            setIsVisible: () => { }, // No-op since we're using the stack
            modalTitle: 'Add New Record',
            mode: 'fullscreen',
            testId: `${testId}.modalView`,
            zIndex,
            onClose: () => {
                // The modal stack will handle removing the modal
                if (onClose) onClose();
            }
        });
    }

    // Filter parent groups (Step 1)
    const parentGroups = useMemo(() => {
        if (!data?.list) return [];

        let filtered = data.list.filter(item =>
            item[parentField.field] === parentField.value &&
            (item[parentId] === null || item[parentId] === undefined || item[parentId] === '')
        );

        if (debouncedSearch && currentStep === 1) {
            const term = debouncedSearch.toLowerCase();
            filtered = filtered.filter(item =>
                item.name?.toLowerCase().includes(term) ||
                item.description?.toLowerCase().includes(term) ||
                item.id?.toLowerCase().includes(term)
            );
        }

        return filtered;
    }, [data?.list, parentField, parentId, debouncedSearch, currentStep]);

    // Filter child groups (Step 2)
    const childGroups = useMemo(() => {
        if (!data?.list || !selectedParent) return [];

        let filtered = data.list.filter(item =>
            item[parentId] === selectedParent.id
        );

        if (debouncedSearch && currentStep === 2) {
            const term = debouncedSearch.toLowerCase();
            filtered = filtered.filter(item =>
                item.name?.toLowerCase().includes(term) ||
                item.description?.toLowerCase().includes(term) ||
                item.id?.toLowerCase().includes(term)
            );
        }

        return filtered;
    }, [data?.list, selectedParent, parentId, debouncedSearch, currentStep]);

    // Step 1 search when groups cannot be picked: every child that matches, in any group
    const matchingChildren = useMemo(() => {
        if (!searchAcross || !data?.list) return [];
        const term = debouncedSearch.toLowerCase();
        return data.list.filter(item =>
            item[parentId] && (
                item.name?.toLowerCase().includes(term) ||
                item.subtitle?.toLowerCase().includes(term) ||
                item.description?.toLowerCase().includes(term)
            )
        );
    }, [data?.list, parentId, debouncedSearch, searchAcross]);

    const parentNameOf = (item) => data?.list?.find(p => p.id === item[parentId])?.name;

    // Get current suggestions based on step
    const suggestions = searchAcross ? matchingChildren : currentStep === 1 ? parentGroups : childGroups;
    const pickingChild = searchAcross || currentStep === 2;

    // Handle parent group selection
    const handleParentSelect = (parent) => {
        setSelectedParent(parent);
        setCurrentStep(2);
        setSearch("");
        setCursor(-1);
    };

    // Handle direct parent selection (+ button)
    const handleDirectParentSelect = (parent) => {
        setIsVisible(false);
        if (onClose) onClose();
        onSelect(parent);
    };

    // Handle child selection
    const handleChildSelect = (child) => {
        setSelectedChild(child);
        // Automatically select and close modal when child is selected
        setIsVisible(false);
        if (onClose) onClose();
        onSelect(child);
    };

    // Handle back to step 1
    const handleBack = () => {
        setCurrentStep(1);
        setSelectedParent(null);
        setSelectedChild(null);
        setSearch("");
        setCursor(-1);
    };

    // Handle save (confirm selection)
    const handleSave = () => {
        const selection = selectedChild || selectedParent;
        if (selection) {
            setIsVisible(false);
            if (onClose) onClose();
            onSelect(selection);
        }
    };

    // Handle cancel
    const handleCancel = () => {
        setIsVisible(false);
        if (onClose) onClose();
    };

    //On Load: Focus on the search Field
    useEffect(() => {
        focusRef.current?.focus();
    }, [currentStep]);

    useEffect(() => {
        if (cursor < 0 || cursor > suggestions.length || !searchResultRef.current) return;
        const listItems = Array.from(searchResultRef.current.children);
        listItems[cursor]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, [cursor]);

    // Reset cursor when suggestions change
    useEffect(() => {
        setCursor(-1);
        if (searchResultRef.current) {
            scrollIntoView(0);
        }
    }, [suggestions]);

    //Monitoring pressing Enter
    const enterPressed = useKeyPress("Enter");
    const escPressed = useKeyPress("Esc");

    //Keyboard events
    const keyboardNavigation = (e) => {
        if (e.key === "Tab") {
            e.preventDefault();
        }

        if (e.key === "ArrowDown") {
            setCursor((c) => (c < suggestions.length - 1 ? c + 1 : c));
        }

        if (e.key === "ArrowUp") {
            setCursor((c) => (c > 0 ? c - 1 : 0));
        }

        if (e.key === "Escape") {
            if (currentStep === 2) {
                handleBack();
            } else {
                handleCancel();
            }
        }

        if (e.key === "Enter" && cursor >= 0) {
            if (pickingChild) {
                handleChildSelect(suggestions[cursor]);
            } else {
                handleParentSelect(suggestions[cursor]);
            }
        }
    };

    useEffect(() => {
        if (enterPressed && cursor >= 0) {
            if (pickingChild) {
                handleChildSelect(suggestions[cursor]);
            } else {
                handleParentSelect(suggestions[cursor]);
            }
        }
    }, [enterPressed, cursor, pickingChild, suggestions]);

    useEffect(() => {
        if (escPressed) {
            if (currentStep === 2) {
                handleBack();
            } else {
                handleCancel();
            }
        }
    }, [escPressed, currentStep]);

    return ReactDOM.createPortal(
        <Modal
            setShow={setIsVisible}
            show={isVisible}
            mode={"fullscreen"}
            className={"fullscreen"}
            minHeight={"35rem"}
            opacity={modalOpacity}
            zIndex={zIndex}
        >
            <Modal.Header setShow={(value) => {
                setIsVisible(value);
                if (!value && onClose) onClose();
            }} onClose={onClose}>
                <h3 style={{ margin: "0px" }}>{t(modalTitle)}</h3>
            </Modal.Header>
            <Modal.Body>
                {currentStep === 2 && (
                    <StepHeader>
                        <IconButton
                            icon={Icon_Arrow_Left}
                            $rounded
                            $variant='secondary'
                            $size='2rem'
                            clickAction={handleBack}
                            testId={`${testId}.back`}
                            ariaLabel='Go back'
                        />
                        <SelectedParentDisplay>
                            {selectedParent?.code || selectedParent?.name || selectedParent?.id}
                        </SelectedParentDisplay>
                        {pickGroup && <IconButton
                            icon={Icon_Check}
                            $rounded
                            $variant='primary'
                            $size='2rem'
                            clickAction={() => handleDirectParentSelect(selectedParent)}
                            testId={`${testId}.selectParent`}
                            ariaLabel='Select parent'
                        />}
                    </StepHeader>
                )}

                <SearchFieldContainer>
                    <SearchField
                        type="text"
                        value={search || ""}
                        name={`${entity}Search`}
                        onChange={handleSearchChange}
                        onKeyDown={keyboardNavigation}
                        ref={focusRef}
                        autoComplete="off"
                        data-testid={`${testId}.searchField`}
                        placeholder={currentStep === 1 ? t('hconnect.modal.search.groups') : t('hconnect.modal.search.subgroups')}
                        aria-label='Search'
                        aria-autocomplete='list'
                        aria-controls='hconnect-menu'
                    />
                    {currentStep === 1 && pickGroup && (
                        <IconButton
                            icon={Icon_Plus}
                            $rounded
                            $variant='primary'
                            $size='2rem'
                            clickAction={clickAction}
                            testId={`${testId}.add`}
                            ariaLabel='Add new record'
                        />
                    )}
                </SearchFieldContainer>

                <SearchResults $hasHeader={currentStep === 2}>
                    <ListGroup ref={searchResultRef} id="hconnect-menu" role="listbox" data-testid={`${testId}.step${currentStep}`}>
                        {suggestions.map((item, idx) => (
                            <li key={idx} role="option" aria-selected={cursor === idx} data-testid={`${testId}.group.${item.id}`}>
                                <MenuItemContent
                                    $active={cursor === idx}
                                    onClick={() => {
                                        if (pickingChild) {
                                            handleChildSelect(item);
                                        } else {
                                            handleParentSelect(item);
                                        }
                                    }}
                                >
                                    <ItemText>
                                        <span>{item.code || item.name || item.id}</span>
                                        {pickingChild && (item.subtitle || searchAcross) && (
                                            <ItemSubtitle>
                                                {[item.subtitle, searchAcross ? parentNameOf(item) : null].filter(Boolean).join(' · ')}
                                            </ItemSubtitle>
                                        )}
                                    </ItemText>
                                    {!pickingChild && !pickGroup && Icon_Arrow_Right}
                                    {!pickingChild && pickGroup && (
                                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                                            <IconButton
                                                icon={Icon_Check}
                                                $rounded
                                                $variant='primary'
                                                $size='1.5rem'
                                                clickAction={(e) => {
                                                    e.stopPropagation();
                                                    handleDirectParentSelect(item);
                                                }}
                                                testId={`${testId}.selectDirect.${item.id}`}
                                                ariaLabel='Select this group'
                                            />
                                            {Icon_Arrow_Right}
                                        </div>
                                    )}
                                    {currentStep === 2 && selectedChild?.id === item.id && (
                                        Icon_Check
                                    )}
                                </MenuItemContent>
                            </li>
                        ))}
                    </ListGroup>
                </SearchResults>

                {currentStep === 2 && (
                    <ActionButtons>
                        <Button
                            $variant='secondary'
                            onClick={handleCancel}
                            data-testid={`${testId}.cancel`}
                        > {t('all.modals.footer.cancel')} </Button>
                        <Button
                            $variant='primary'
                            onClick={handleSave}
                            data-testid={`${testId}.save`}
                            disabled={pickGroup ? (!selectedChild && !selectedParent) : !selectedChild}
                        > {t('all.modals.footer.save')} </Button>
                    </ActionButtons>
                )}
            </Modal.Body>
        </Modal>
        , document.body);
};

export default HConnectModal;
